import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Loader2,
  ChevronUp,
  ChevronDown,
  User,
  CheckCircle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import SmartPagination from '@/components/ui/smart-pagination';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import { externalPatientService } from '@/services/externalPatientService';
import { PaginationParams } from '@/lib/types';
import { getDefaultContactValue, toTitleCase, formatToLocalDate } from '@/lib/utils';
import { formatDOB } from '@/lib/dateUtils';

// Sourced from VITE_NIMBUS_CLINIC_ID; must match the value used in PrectitionerPortal sidebar gating.
const NIMBUS_CLINIC_ID = (import.meta.env.VITE_NIMBUS_CLINIC_ID as string | undefined) || '';

const ExternalPatients = () => {
  const navigate = useNavigate();
  const { clinic } = useClinic();

  // Defensive gate: non-Nimbus clinics should not see this page even via direct URL.
  useEffect(() => {
    if (clinic?.id && clinic.id !== NIMBUS_CLINIC_ID) {
      navigate('/practitioner-portal', { replace: true });
    }
  }, [clinic?.id, navigate]);

  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 25,
    totalItems: 0,
    itemsPerPage: 25,
    totalPages: 0,
    sortBy: 'added_at',
    sortOrder: 'DESC',
    filter: {},
  });

  const handleSort = (field: string) => {
    if (field === pagination.sortBy) {
      setPagination(prev => ({
        ...prev,
        page: 1,
        sortOrder: pagination.sortOrder === 'ASC' ? 'DESC' : 'ASC',
      }));
    } else {
      setPagination(prev => ({
        ...prev,
        page: 1,
        sortBy: field,
        sortOrder: 'ASC',
      }));
    }
  };

  const currentPage = pagination.page || 1;
  const handlePageChange = (page: number) => setPagination({ ...pagination, page });

  const getStatusBadge = (status: string) => {
    if (status === 'active') {
      return (
        <Badge
          className='inline-flex w-fit items-center gap-1 border-0 bg-emerald-600 px-2 py-0.5 font-medium text-white hover:bg-emerald-600'
          style={{ fontSize: '0.7rem' }}
        >
          <CheckCircle className='h-3 w-3' />
          Active
        </Badge>
      );
    }
    return (
      <Badge
        className='inline-flex w-fit items-center border-0 bg-slate-500 px-2 py-0.5 font-medium text-white hover:bg-slate-500'
        style={{ fontSize: '0.7rem' }}
      >
        {toTitleCase(status || '--')}
      </Badge>
    );
  };

  const fetchExternalPatients = async () => {
    setIsLoading(true);
    try {
      const response = await externalPatientService.getExternalPatients({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        sortBy: pagination.sortBy,
        sortOrder: (pagination.sortOrder?.toUpperCase() as 'ASC' | 'DESC') || 'DESC',
        filter: {},
      });
      setPatients(response.data.patients || []);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to fetch external patients',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (clinic?.id !== NIMBUS_CLINIC_ID) return;
    fetchExternalPatients();
  }, [
    clinic?.id,
    pagination.page,
    pagination.limit,
    searchTerm,
    pagination.sortBy,
    pagination.sortOrder,
  ]);

  const SortIcon = ({ field }: { field: string }) => {
    if (pagination.sortBy !== field) return <ChevronUp className='h-4 w-4 text-slate-300' />;
    return pagination.sortOrder === 'ASC' ? (
      <ChevronUp className='h-4 w-4 text-slate-700' />
    ) : (
      <ChevronDown className='h-4 w-4 text-slate-700' />
    );
  };

  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-600' },
    { bg: 'bg-purple-100', text: 'text-purple-600' },
    { bg: 'bg-pink-100', text: 'text-pink-600' },
    { bg: 'bg-orange-100', text: 'text-orange-600' },
    { bg: 'bg-amber-100', text: 'text-amber-600' },
    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    { bg: 'bg-rose-100', text: 'text-rose-600' },
    { bg: 'bg-violet-100', text: 'text-violet-600' },
    { bg: 'bg-sky-100', text: 'text-sky-600' },
  ];

  return (
    <div className='space-y-4 sm:space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div>
          <h1 className='font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl'>
            External Patients
          </h1>
          <p className='mt-1 text-sm text-slate-500'>
            Patients from partner clinics who have granted Nimbus access for guided orders
          </p>
        </div>
      </div>

      {/* Search */}
      <div className='flex flex-row items-center gap-3'>
        <div className='relative max-w-md flex-1'>
          <Search className='absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400' />
          <Input
            placeholder='Search by name, email, phone, patient ID, or DOB'
            className='rounded-lg border-slate-200 bg-white pl-9'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value.trimStart())}
          />
        </div>
      </div>

      {/* Table */}
      <div className='overflow-hidden rounded-md border border-slate-200 bg-white'>
        <div className='overflow-x-auto'>
          <table className='w-full min-w-[1100px] text-sm'>
            <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
              <tr className='border-b border-slate-200'>
                <th
                  className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  #
                </th>
                <th
                  className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('name')}
                >
                  <div className='flex items-center gap-1'>
                    Patient Name
                    <SortIcon field='name' />
                  </div>
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Gender
                </th>
                <th
                  className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('dob')}
                >
                  <div className='flex items-center gap-1'>
                    DOB
                    <SortIcon field='dob' />
                  </div>
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Email
                </th>
                <th
                  className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                  style={{ fontSize: '0.7rem' }}
                >
                  Phone
                </th>
                <th
                  className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('clinic_name')}
                >
                  <div className='flex items-center gap-1'>
                    Origin Clinic
                    <SortIcon field='clinic_name' />
                  </div>
                </th>
                <th
                  className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('added_at')}
                >
                  <div className='flex items-center gap-1'>
                    Added On
                    <SortIcon field='added_at' />
                  </div>
                </th>
                <th
                  className='cursor-pointer px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700 hover:text-slate-900'
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('status')}
                >
                  <div className='flex items-center gap-1'>
                    Status
                    <SortIcon field='status' />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className='h-48 text-center'>
                    <div className='flex flex-col items-center justify-center text-slate-500'>
                      <Loader2 className='mb-3 h-8 w-8 animate-spin text-brand-teal' />
                      <p className='text-sm font-medium'>Loading external patients...</p>
                    </div>
                  </td>
                </tr>
              ) : patients.length === 0 ? (
                <tr>
                  <td colSpan={9} className='h-48 text-center'>
                    <div className='flex flex-col items-center justify-center text-slate-500'>
                      <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100'>
                        <Users className='h-7 w-7 text-slate-400' />
                      </div>
                      <p className='font-medium text-slate-600'>
                        {searchTerm ? 'No external patients found' : 'No external patients yet'}
                      </p>
                      <p className='mt-1 text-sm text-slate-400'>
                        {searchTerm
                          ? 'Try adjusting your search'
                          : 'External patients appear here when partner clinics grant access'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                patients.map((patient, index) => {
                  const iconColor = colors[index % colors.length];
                  return (
                    <tr
                      key={patient.id}
                      className='cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/80'
                      onClick={() => navigate(`/practitioner-portal/external-patients/${patient.id}`)}
                    >
                      <td className='px-4 py-3 text-slate-500' style={{ fontSize: '0.8rem' }}>
                        #{String((currentPage - 1) * (pagination.limit || 25) + index + 1).padStart(3, '0')}
                      </td>
                      <td className='max-w-[200px] px-4 py-3' style={{ fontSize: '0.8rem' }}>
                        <div className='flex items-center gap-3'>
                          <div
                            className={`h-8 w-8 ${iconColor.bg} flex flex-shrink-0 items-center justify-center rounded-lg`}
                          >
                            <User className={`h-4 w-4 ${iconColor.text}`} />
                          </div>
                          <span className='block max-w-[150px] truncate font-medium capitalize text-slate-900'>
                            {patient.full_name}
                          </span>
                        </div>
                      </td>
                      <td className='px-4 py-3 text-slate-900' style={{ fontSize: '0.8rem' }}>
                        {toTitleCase(patient?.gender || '--')}
                      </td>
                      <td className='px-4 py-3 text-slate-900' style={{ fontSize: '0.8rem' }}>
                        {patient?.dob ? formatDOB(patient.dob) : '--'}
                      </td>
                      <td
                        className='max-w-[180px] px-4 py-3 text-slate-900'
                        style={{ fontSize: '0.8rem' }}
                      >
                        <span
                          className='block truncate'
                          title={getDefaultContactValue(patient?.contacts, 'email') || '-'}
                        >
                          {getDefaultContactValue(patient?.contacts, 'email') || '--'}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-slate-900' style={{ fontSize: '0.8rem' }}>
                        {getDefaultContactValue(patient?.contacts, 'phone') || '--'}
                      </td>
                      <td
                        className='max-w-[160px] px-4 py-3 text-slate-900'
                        style={{ fontSize: '0.8rem' }}
                      >
                        <span className='block truncate' title={patient?.clinic?.name || '-'}>
                          {patient?.clinic?.name || '--'}
                        </span>
                      </td>
                      <td className='px-4 py-3 text-slate-900' style={{ fontSize: '0.8rem' }}>
                        {patient?.externalAccess?.[0]?.created_at
                          ? formatToLocalDate(patient.externalAccess[0].created_at, 'MMM DD, YYYY')
                          : '--'}
                      </td>
                      <td className='px-4 py-3'>{getStatusBadge(patient.status)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className='flex flex-col items-center gap-3 border-t border-slate-200 px-2 py-3 sm:flex-row sm:justify-between sm:px-4'>
            <div className='w-full overflow-x-auto sm:w-auto'>
              <SmartPagination
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                className='mx-0 justify-center sm:justify-start'
                maxVisiblePages={3}
              />
            </div>
            <p className='whitespace-nowrap text-xs text-slate-500'>
              Total:{' '}
              <span className='font-medium text-slate-700'>{pagination.totalItems}</span> External
              Patients
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExternalPatients;
